import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';

import Api from 'api';
import * as Common from 'common/components';
import { getCompany, getSubcompany, getPermissions, getUser } from 'auth/selectors';
import { addGrowlError, addGrowlSuccess } from 'messages/actions';
import { custodyVoucherPreview } from 'lib/pdfs/autosur';
import {
  VOUCHER_FORMATS,
  canSignGeneralVoucher,
  downloadGeneralVoucher,
  generalVoucherHasPrice,
  saveSignedCopy,
  signGeneralVoucher,
  voucherConfig,
} from 'lib/vouchers';
import { getEmployeesAssetsReportFilters } from 'reports/selectors';

/**
 * With a search, the report row holds only the assets that match it, and the
 * back rejects a voucher that does not cover ALL the custodian's active assets
 * (409 error.custody_voucher.incomplete). So signing is disabled while a search
 * is applied (user's decision of 2026-11-02).
 */
export const isSearchActive = (filters) =>
  !!(filters && String(filters.fulltext == null ? '' : filters.fulltext).trim());

/**
 * "Firmar y guardar" of Reportes > Vales de resguardo, for one employee: the
 * client's format with all the assets of the row, signed and saved, locked, in
 * the files of each asset (POST .../custody_vouchers). Only for clients with
 * signGeneral (lib/vouchers/clientConfig) and users who can edit assets, the
 * same permission the back asks for. Used in the desktop row and in the phone
 * card (Common.List). The modal is mounted on the first click. Disabled, with a
 * note to clear it, while the report has a search applied (isSearchActive).
 */
export class EmployeeVoucherSign extends Component {
  static propTypes = {
    record: PropTypes.object.isRequired,
    className: PropTypes.string,
    company: PropTypes.object,
    subcompany: PropTypes.object,
    permissions: PropTypes.object,
    user: PropTypes.object,
    searching: PropTypes.bool,
    error: PropTypes.func,
    success: PropTypes.func,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = { className: 'btn btn-outline-success btn-sm', permissions: {} };

  state = { opened: false, show: false };

  ctx = () => ({
    company: this.props.company,
    subcompany: this.props.subcompany,
    user: this.props.user,
  });

  // AutoSur shows its voucher (Common.AutosurVoucherPreview), the rest a summary.
  renderPreview = () => {
    const { record, company, subcompany, user } = this.props;
    if (voucherConfig(company, subcompany).individualFormat === VOUCHER_FORMATS.AUTOSUR) {
      return (
        <Common.AutosurVoucherPreview
          preview={custodyVoucherPreview(record, { subcompany: subcompany.name, user })}
        />
      );
    }
    return (
      <Common.VoucherSummary
        custodian={[record.name, record.lastname].filter(Boolean).join(' ')}
        position={record.position}
        assets={record.assets}
      />
    );
  };

  handleOpen = (e) => {
    e.preventDefault();
    this.setState({ opened: true, show: true });
  };

  handleClose = () => this.setState({ show: false });

  handlePaper = (withPrice) => downloadGeneralVoucher(this.props.record, this.ctx(), { withPrice });

  handleSign = async (signatureImage, withPrice) => {
    const { record, company, subcompany, user } = this.props;
    const { blob, fileName } = await signGeneralVoucher(record, this.ctx(), signatureImage, user, {
      withPrice,
    });
    await Api.assets.addEmployeeCustodyVoucher(
      company.id,
      subcompany.id,
      record.id,
      (record.assets || []).map((asset) => asset.id),
      blob,
      fileName
    );
    saveSignedCopy(blob, fileName); // only once the back saved it
  };

  render = () => {
    const { company, subcompany, permissions, t } = this.props;
    if (!canSignGeneralVoucher(company, subcompany) || !(permissions || {})['acl.assets.update']) {
      return null;
    }
    if (this.props.searching) {
      return (
        <span className="d-inline-block align-top text-center">
          <button type="button" className={this.props.className} disabled>
            {t('custodyVoucher.signGeneral')}
          </button>
          <small className="d-block text-muted">{t('custodyVoucher.clearSearchToSign')}</small>
        </span>
      );
    }
    return (
      <Fragment>
        <a className={this.props.className} href="#sign" onClick={this.handleOpen}>
          {t('custodyVoucher.signGeneral')}
        </a>
        {this.state.opened ? (
          <Common.SignVoucherModal
            show={this.state.show}
            handleClose={this.handleClose}
            title={t('actions.custodyVoucher')}
            preview={this.renderPreview()}
            priceOption={generalVoucherHasPrice(company, subcompany)}
            onPaper={this.handlePaper}
            onSign={this.handleSign}
            successMessage="success.employee_custody_voucher_saved"
            error={this.props.error}
            success={this.props.success}
          />
        ) : null}
      </Fragment>
    );
  };
}

export const mapStateToProps = (state) => ({
  company: getCompany(state),
  subcompany: getSubcompany(state),
  permissions: getPermissions(state),
  user: getUser(state),
  searching: isSearchActive(getEmployeesAssetsReportFilters(state)),
});

const mapDispatchToProps = { error: addGrowlError, success: addGrowlSuccess };

export default connect(mapStateToProps, mapDispatchToProps)(translate()(EmployeeVoucherSign));



// WEBPACK FOOTER //
// src/modules/secured/modules/reports/containers/EmployeeVoucherSign.js